import {UntypedFormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';
import { AppointmentType } from '@signal/asp-data-commons';

/**
 * Custom validator to do the OR condition validation in select vehicle form (Step 1)
 */
export function chooseVehicleValidator(apptType?, isVehicleSelectionOptional = false): ValidatorFn {
  return (group: UntypedFormGroup): ValidationErrors => {
    const isOwnVehicle = group.controls.isOwnVehicle;
    const isInputVinBased = group.controls.isInputVinBased;
    const vin = group.controls.vin;
    const make = group.controls.make;
    const otherMake = group.controls.otherMake;
    const year = group.controls.year;
    const model = group.controls.model;
    const mileage = group.controls.mileage;
    const orderRefNumber = group.controls.orderRefNumber;
    const dealerOrderNumber = group.controls.dealerOrderNumber;

    const updateOneOfRequiredError = (control, shouldSet: boolean) => {
      if (!control) {
        return;
      }
      const existingErrors = control.errors || {};
      if (shouldSet) {
        control.setErrors({ ...existingErrors, requiredOneOf: true });
        return;
      }

      if (existingErrors.requiredOneOf) {
        const { requiredOneOf, ...rest } = existingErrors;
        control.setErrors(Object.keys(rest).length ? rest : null);
      }
    };

    if (isVehicleSelectionOptional) {
      // In parts flow, vehicle identification is optional.
      // If make is selected, year and model become required.
      const hasOrderRef = !!orderRefNumber?.value?.toString().trim();
      const hasDealerOrder = !!dealerOrderNumber?.value?.toString().trim();
      const missingBothOrderRefs = !hasOrderRef && !hasDealerOrder;
      updateOneOfRequiredError(orderRefNumber, missingBothOrderRefs);
      updateOneOfRequiredError(dealerOrderNumber, missingBothOrderRefs);

      vin.setErrors(null);
      if (!make.value) {
        make.setErrors(null);
        otherMake.setErrors(null);
        year.setErrors(null);
        model.setErrors(null);
        return null;
      }

      make.setErrors(null);
      if (!otherMake.value && make.value === 'Other') {
        otherMake.setErrors({required: 'service.makeNameIsRequired'});
      } else {
        otherMake.setErrors(null);
      }

      if (!year.value) {
        year.setErrors({required: 'service.yearIsRequired'});
      } else {
        year.setErrors(null);
      }

      if (!model.value) {
        model.setErrors({required: 'service.modelIsRequired'});
      } else {
        model.setErrors(null);
      }
      return null;
    }

    if (isOwnVehicle.value) {
      if(!vin.value) {
           vin.setErrors(null);
          return {required: 'service.vehicleIsRequired'};
      } else if (!+(mileage.value)) {
        if(apptType !== 'Learning'){
        return {required: 'service.mileageIsRequired'};
        }
      } else {
        return null;
      }
    } else {
      if (isInputVinBased.value) {
        if (!vin.value) {
          vin.setErrors({required: 'service.vinIsRequired'});
        } else {
          if (!/^([a-zA-Z0-9]{17})$/.test(vin.value)) {
            vin.setErrors({pattern17DigitAndAlphaNumeric: 'service.vinShouldBeDigitAndAlphanumeric'});
          } else if (!/^(.{17})$/.test(vin.value)) {
            vin.setErrors({pattern17Digit: 'service.vinShouldBeDigit'});
          } else if (!/^([a-zA-Z0-9]*)$/.test(vin.value)) {
            vin.setErrors({patternAlphaNumeric: 'service.vinShouldBeAlphanumeric'});
          }
        }

      } else {
        if (!make.value) {
          make.setErrors({required: 'service.makeIsRequired'});
        }
        if (!otherMake.value && make.value === 'Other') {
          otherMake.setErrors({required: 'service.makeNameIsRequired'});
        } else {
          otherMake.setErrors(null);
        }
        if (!year.value) {
          year.setErrors({required: 'service.yearIsRequired'});
        }
        if (!model.value) {
          model.setErrors({required: 'service.modelIsRequired'});
        }
      }
      if (!+(mileage.value)) {
        if(apptType !== 'Learning'){
        mileage.setErrors({required: 'service.mileageIsRequired'});
        }
      }
    }

    return;
  };
}

/**
 * Select service validator
 */
export function getServiceRequestValidator(): ValidatorFn {
  return (group: UntypedFormGroup): ValidationErrors => {
    const servicesSelected: any[] = group.value?.allServices?.filter((item) => item.selected).map((item) => item.id);
    const recommendedServicesSelected: any[] = group.value?.recommendedServices?.filter((item) => item.selected).map((item) => item.id);
    if (!(servicesSelected.length + recommendedServicesSelected.length)) {
      return {serviceRequired: 'service.atLeastOneServiceIsRequired'};
    } else {
      return null;
    }
  };
}
